import React, { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description?: string;
  /** Pfad der Seite, z. B. "/services". Standard: aktueller Pfad */
  canonicalPath?: string;
  /** Rechtliche Seiten: nicht in den Suchindex aufnehmen */
  noindex?: boolean;
}

export const BASE_URL = 'https://top-bhs.de';

/** Absolute Canonical-URL: https, ohne www, ohne Query/Hash, ohne Slash am Ende (außer Startseite) */
export function canonicalUrl(path: string): string {
  let p = (path || '/').split(/[?#]/)[0];
  if (!p.startsWith('/')) p = `/${p}`;
  if (p.length > 1) p = p.replace(/\/+$/, '');
  return `${BASE_URL}${p}`;
}

/** Legt ein <meta>/<link>-Tag an oder aktualisiert das vorhandene (keine Duplikate) */
function upsert(tag: 'meta' | 'link', key: 'name' | 'property' | 'rel', keyValue: string, attr: 'content' | 'href', value: string) {
  const all = document.head.querySelectorAll(`${tag}[${key}="${keyValue}"]`);
  all.forEach((el, i) => { if (i > 0) el.remove(); });
  let el = all[0] as HTMLElement | undefined;
  if (!el) {
    el = document.createElement(tag);
    el.setAttribute(key, keyValue);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

/**
 * Setzt Title und SEO-Tags direkt im <head>. Direkt (nicht über Helmet), damit react-snap
 * die Werte beim Vorrendern sicher übernimmt und keine doppelten Tags entstehen.
 */
const SEOHead: React.FC<SEOHeadProps> = ({ title, description, canonicalPath, noindex = false }) => {
  const location = useLocation();
  const canonical = canonicalUrl(canonicalPath ?? location.pathname);

  useLayoutEffect(() => {
    document.title = title;
    upsert('link', 'rel', 'canonical', 'href', canonical);
    upsert('meta', 'name', 'robots', 'content', noindex ? 'noindex, follow' : 'index, follow');
    upsert('meta', 'property', 'og:url', 'content', canonical);
    upsert('meta', 'property', 'og:title', 'content', title);
    upsert('meta', 'name', 'twitter:title', 'content', title);
    if (description) {
      upsert('meta', 'name', 'description', 'content', description);
      upsert('meta', 'property', 'og:description', 'content', description);
      upsert('meta', 'name', 'twitter:description', 'content', description);
    }
  }, [title, description, canonical, noindex]);

  return null;
};

export default SEOHead;
